/** What an action locks, shown before the user confirms it. Mirrors the API's lock rules. */
export interface ClaimLockNotice {
  title: string;
  /** What can't change once it's done. */
  locks: string[];
  /** What can still change, and until when. */
  editable: string[];
  /** A few words for the collapsed undo section, e.g. "No". */
  undoSummary: string;
  /** Whether it can be undone at all; shown as the summary's colour. */
  reversible: boolean;
  /** Whether and how it can be undone. */
  undo: string;
  /** What to check before continuing. */
  verify: string[];
}

const plural = (count: number, one: string, many: string) => (count === 1 ? one : `${count} ${many}`);

/** Copy for the confirmations before resolving a job, authorizing a claim, or paying it. */
export const CLAIM_LOCK_CONFIG = {
  labels: {
    locks: 'Will be locked',
    editable: 'Can still change',
    undo: 'Can it be undone?',
    verify: 'Check before continuing',
  },
  cancel: 'Cancel',

  resolve: {
    header: {
      title: 'Resolve job',
      subtitle: (job: string) => `Resolving <strong>${job}</strong> changes what can be edited on it.`,
    },
    confirm: 'Resolve job',
    /** Only jobs with charges: without them the decision stays open until the claim is authorized. */
    notice: {
      title: 'This job will be marked as resolved',
      locks: [
        'The decision and the approved amount of each charge.',
        "The charges: the job can't be sent back to review to add, change or remove them.",
        'Evidence holds and escalations: none can be opened.',
      ],
      editable: [
        'The resolution and amounts, until the claim is authorized.',
        'The cure, until the claim is authorized.',
      ],
      undoSummary: "Can't be undone",
      reversible: false,
      undo: "No. A resolved job can't go back to review. If the decision is wrong, add a new job instead.",
      verify: [
        'Every charge is decided and its approved amount is final.',
        'The actual cost matches the repair receipt.',
      ],
    } satisfies ClaimLockNotice,
  },

  authorize: {
    /** A supplemental authorization covers only the jobs added after a payment. */
    notice: (jobs: number, isSupplemental: boolean): ClaimLockNotice => ({
      title: `${plural(jobs, 'This job', 'jobs')} will be locked`,
      locks: [
        `The ${plural(jobs, isSupplemental ? 'new job' : 'job', isSupplemental ? 'new jobs' : 'jobs')}: decisions, charges, cures and resolutions.`,
        'The claim details, payee and repair shop.',
        'New jobs, until a payment is issued from this authorization.',
      ],
      editable: ['Notes and documents.', 'Payments, which are prepared from this authorization.'],
      undoSummary: 'Until a payment is issued',
      reversible: true,
      undo: 'Yes, until a payment is issued from it. Reversing keeps a record, voids its draft payments and unlocks its jobs. Once a payment is issued it can no longer be reversed.',
      verify: ['Every job is approved or rejected with the right amounts.', 'The payee and net payable below.'],
    }),
  },

  payment: {
    issue: {
      header: {
        title: 'Issue payment',
        subtitle: (title: string) => `Send <strong>${title}</strong> to the payee.`,
      },
      confirm: 'Issue payment',
    },
    markPaid: {
      header: {
        title: 'Mark as paid',
        subtitle: (title: string) => `Record <strong>${title}</strong> as paid to the repair shop.`,
      },
      confirm: 'Mark as paid',
    },
    /** Claims in the previous flow are paid without an authorization. */
    notice: (usesAuthorization: boolean): ClaimLockNotice => ({
      title: 'This payment is final',
      locks: usesAuthorization
        ? [
            'The authorization it pays: it can no longer be reversed.',
            'The jobs that authorization covers, for good.',
            'The claim details. Only the diagnosis, cause of failure and correction can change, and only after a new job is added.',
          ]
        : ['The payment: its amount, payee and destination.'],
      editable: usesAuthorization
        ? ['New jobs can be added and authorized separately.', 'Notes and documents.']
        : ['Notes and documents.'],
      undoSummary: "Can't be undone",
      reversible: false,
      undo: "No. A payment that's issued or marked as paid can't be voided or edited.",
      verify: ['The payee and where the money goes.', 'The amount.'],
    }),
  },
};
